import { searchSites } from './elasticsearchApi';

/**
 * Elasticsearch health check utility
 * Provides lightweight connectivity testing and caching
 */

// Cache for health status
let healthCache = {
  isHealthy: null,
  lastChecked: null,
  checkInProgress: false
};

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

/**
 * Check if Elasticsearch is healthy
 * @param {boolean} forceCheck - Force a new check even if cache is valid
 * @returns {Promise<boolean>} True if Elasticsearch is available
 */
export const checkElasticsearchHealth = async (forceCheck = false) => {
  const now = Date.now();
  
  // Return cached result if still valid and not forcing a check
  if (!forceCheck && 
      healthCache.lastChecked && 
      (now - healthCache.lastChecked) < CACHE_DURATION && 
      healthCache.isHealthy !== null) {
    return healthCache.isHealthy;
  }

  // Prevent multiple simultaneous checks
  if (healthCache.checkInProgress) {
    // Wait for ongoing check to complete
    let attempts = 0;
    while (healthCache.checkInProgress && attempts < 50) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    return healthCache.isHealthy !== null ? healthCache.isHealthy : false;
  }

  healthCache.checkInProgress = true;

  try {
    // Perform a minimal search to test connectivity
    const result = await Promise.race([
      searchSites({
        query: '',
        page: 0,
        size: 1,
        includeAggregations: false
      }),
      // Timeout after 5 seconds
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Health check timeout')), 5000)
      )
    ]);

    const isHealthy = result && result.success === true;
    
    // Update cache
    healthCache = {
      isHealthy,
      lastChecked: now,
      checkInProgress: false
    };

    console.log(`Elasticsearch health check: ${isHealthy ? 'HEALTHY' : 'UNHEALTHY'}`);
    return isHealthy;

  } catch (error) {
    console.warn('Elasticsearch health check failed:', error.message);
    
    // Update cache with failure
    healthCache = {
      isHealthy: false,
      lastChecked: now,
      checkInProgress: false
    };

    return false;
  }
};

/**
 * Get cached health status without triggering a new check
 * @returns {boolean|null} Cached health status, null if no cache
 */
export const getCachedHealthStatus = () => {
  return healthCache.isHealthy;
};

/**
 * Clear the health cache (useful for testing or forced refresh)
 */
export const clearHealthCache = () => {
  healthCache = {
    isHealthy: null,
    lastChecked: null,
    checkInProgress: false
  };
};

/**
 * Background health checker that periodically updates the cache
 * Call this once during app initialization to keep health status current
 */
export const startBackgroundHealthCheck = () => {
  // Initial check
  checkElasticsearchHealth();
  
  // Periodic checks every 2 minutes
  const intervalId = setInterval(() => {
    checkElasticsearchHealth();
  }, 2 * 60 * 1000);

  // Return cleanup function
  return () => clearInterval(intervalId);
};
